import { Label } from '@yleisradio/areena-types';
import { CardList, ListElement, UiCard, UiLabel } from './viewTypes';
import {
  getContinueCardUiProgressBarFromLabels,
  getLiveCardUiProgressBar,
  getProgressDurationLabel,
  isCurrentBroadcastCard,
} from '../utils/cardProgress';
import { findFormattedLabel, labelsOf } from '../utils/labels';
import { CardWithPointer } from '../../store/view/types';
import { getDataAttributesImpression } from '../analytics/getDataAttributesImpression';
import { isHeroList, isSquareList } from '../utils/list';
import { getGradientBackground } from './list/listStyle';
import {
  getCardSecondaryText,
  getCardPrimaryText,
  getLimelightTitle,
  getLimelightDescription,
  getCardAriaLabel,
} from './card/textUtils';
import { getCardListImage, getLimelightImages } from './card/imageUtils';
import { getBroadcastAttributes } from './card/broadcastAttributes';
import { idFromPointer } from '../utils/pointer';
import parseIsoDuration from 'parse-iso-duration';

function getLimelightLabelsBroadcast(labels: Label[] | undefined): UiLabel[] {
  return (
    [
      'broadcastStartDate',
      'onDemandStartDate',
      'duration',
      'broadcastService',
      'onDemandEndDate',
    ] as const
  ).reduce((uiLabels: UiLabel[], labelType) => {
    const text = findFormattedLabel(labelType, labels)?.formatted;

    if (text) {
      return uiLabels.concat({ text });
    }

    return uiLabels;
  }, [] satisfies UiLabel[]);
}
const getLabelsHighlight = (labels: Label[] | undefined) => {
  return labelsOf('highlight', labels).map(currentLabel => ({
    color: currentLabel.color,
    backgroundColor: currentLabel.backgroundColor,
    text: currentLabel.formatted,
  }));
};

type ParseCardParams = {
  card: CardWithPointer;
  listIndex: number;
  cardIndex: number;
  comscore: Record<string, string> | undefined;
  isGeoBlocked: boolean;
  isBlocked: boolean;
  cardList: CardList;
};

export function parseAPICard({
  card,
  listIndex,
  cardIndex,
  comscore,
  isGeoBlocked,
  isBlocked,
  cardList,
}: ParseCardParams): ListElement & { data: UiCard } {
  const uiLabels: UiLabel[] = labelsOf('highlight', card.labels).map(label => ({
    ...label,
    text: label.formatted || '',
  }));

  const limelightImages = getLimelightImages({ card, cardList });

  let duration: number | undefined;
  let itemId: string | undefined;

  if (cardList.isContinueList) {
    const { raw, pointer } = getProgressDurationLabel(card.labels) || {};

    if (raw) {
      try {
        duration = parseIsoDuration(raw) / 1000;
      } catch {}
    }

    if (pointer) {
      itemId = idFromPointer(pointer);
    }
  }

  return {
    data: {
      limelight: {
        image: limelightImages.image,
        logoImage: limelightImages.logo,
        description: getLimelightDescription(card),
        labelsHighlight: isHeroList(cardList.style)
          ? getLabelsHighlight(card.labels)
          : undefined,
        labelsBroadcast: getLimelightLabelsBroadcast(card.labels),
        title: getLimelightTitle(card),
      },
      primaryText: getCardPrimaryText({ card, cardList }),
      secondaryText: getCardSecondaryText({ card, cardList }),
      ariaLabel: getCardAriaLabel({
        card,
        isContinueList: !!cardList.isContinueList,
        uiLabels,
      }),
      progress: isCurrentBroadcastCard(card)
        ? getLiveCardUiProgressBar(card, isSquareList(cardList.style))
        : getContinueCardUiProgressBarFromLabels(
            card,
            !!cardList.isContinueList
          ),
      dataAttributesBroadcast: getBroadcastAttributes(card),
      image: getCardListImage({ cardList, card }),
      pointer: card.pointer,
      labels: uiLabels,
      dataAttributesImpression: getDataAttributesImpression({
        listIndex,
        cardIndex,
        pointer: card.pointer,
        comscore,
      }),
      persistentState: card.persistentState,
      contentBackground: !Boolean(card.image)
        ? getGradientBackground(cardList.style?.image, card.color)
        : undefined,
      isBlocked: isBlocked,
      isGeoBlocked: isGeoBlocked,
      listStyle: cardList.style,
      presentation: card.presentation,
      menuSource: card.menuSource,
      itemId,
      duration,
    } satisfies UiCard,
    cardApiIndex: cardIndex,
  };
}
